/**
 * The icon set, drawn rather than borrowed.
 *
 * One family, one stroke weight (1.5 at 20px, scaling with the box), `currentColor` throughout so
 * an icon always agrees with the text beside it. DESIGN.md forbids emoji and unicode glyphs
 * standing in for icons, which the sibling Torahly page does throughout; this is the alternative,
 * and it is small enough that no icon library needs to ship with the portal.
 */

interface IconProps {
  size?: number
  className?: string
  /**
   * An override for the one case `currentColor` cannot serve: an icon that must differ from the
   * text beside it, such as a per-ingredient verdict mark inside an ordinary line of body copy.
   */
  style?: React.CSSProperties
}

function svg(path: React.ReactNode, { size = 20, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  )
}

export const Shield = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3 5 6v5.5c0 4 2.9 7.6 7 9.5 4.1-1.9 7-5.5 7-9.5V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </>,
    p,
  )

export const Check = (p: IconProps) => svg(<path d="m5 12.5 4.5 4.5L19 7" />, p)

export const Cross = (p: IconProps) =>
  svg(
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </>,
    p,
  )

export const Alert = (p: IconProps) =>
  svg(
    <>
      <path d="M12 4.5 2.8 20h18.4L12 4.5Z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </>,
    p,
  )

export const Search = (p: IconProps) =>
  svg(
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4 4" />
    </>,
    p,
  )

export const Moon = (p: IconProps) => svg(<path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />, p)

export const Calendar = (p: IconProps) =>
  svg(
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="3" />
      <path d="M3.5 10h17" />
      <path d="M8 3.5v3M16 3.5v3" />
    </>,
    p,
  )

export const Drop = (p: IconProps) => svg(<path d="M12 3.5c3.2 3.6 5.5 6.4 5.5 9.3a5.5 5.5 0 0 1-11 0c0-2.9 2.3-5.7 5.5-9.3Z" />, p)

export const Book = (p: IconProps) =>
  svg(
    <>
      <path d="M4 5.5A2 2 0 0 1 6 3.5h13v14H6a2 2 0 0 0-2 2v-14Z" />
      <path d="M4 19.5a2 2 0 0 1 2-2h13v3H6a2 2 0 0 1-2-2Z" />
    </>,
    p,
  )

export const Sparkle = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3.5 13.6 9 19 10.6 13.6 12.2 12 17.7 10.4 12.2 5 10.6 10.4 9 12 3.5Z" />
      <path d="M18.5 16.5 19.2 19l2.3.7-2.3.8-.7 2.4-.8-2.4-2.2-.8 2.2-.7.8-2.5Z" />
    </>,
    p,
  )

export const QrIcon = (p: IconProps) =>
  svg(
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <path d="M13.5 13.5h3v3h-3z" />
      <path d="M20.5 13.5v3M17.5 20.5h3M13.5 20.5h.01" />
    </>,
    p,
  )

export const Upload = (p: IconProps) =>
  svg(
    <>
      <path d="M12 16V4.5" />
      <path d="m7.5 9 4.5-4.5L16.5 9" />
      <path d="M4.5 15v3a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3" />
    </>,
    p,
  )

export const Download = (p: IconProps) =>
  svg(
    <>
      <path d="M12 4.5V16" />
      <path d="m7.5 11.5 4.5 4.5 4.5-4.5" />
      <path d="M4.5 15v3a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3" />
    </>,
    p,
  )

export const Phone = (p: IconProps) =>
  svg(
    <>
      <rect x="6.5" y="2.5" width="11" height="19" rx="3" />
      <path d="M10.5 18.5h3" />
    </>,
    p,
  )

export const Laptop = (p: IconProps) =>
  svg(
    <>
      <rect x="4" y="5" width="16" height="10.5" rx="2" />
      <path d="M2.5 19h19" />
    </>,
    p,
  )

export const Unplugged = (p: IconProps) =>
  svg(
    <>
      <path d="M2.5 2.5l19 19" />
      <path d="M5 12.8a10 10 0 0 1 3.2-2.1" />
      <path d="M2 9.2a15 15 0 0 1 4.4-2.8" />
      <path d="M19 12.8a10 10 0 0 0-4.6-2.5" />
      <path d="M22 9.2a15 15 0 0 0-8.2-3.8" />
      <path d="M9.5 16.3a5 5 0 0 1 5 0" />
      <path d="M12 20h.01" />
    </>,
    p,
  )

export const Chevron = (p: IconProps) => svg(<path d="m9 5.5 7 6.5-7 6.5" />, p)
